import { useEffect, useMemo, useRef } from "react";

import { useSite } from "~/contexts";
import { bySiteId } from "~/hec/utils/site-id";
import { getMostRecentNonActiveGoal } from "~/hooks/learning-center/helpers";
import useLearningCenter from "~/hooks/use-learning-center";
import { safeSessionStorage } from "~/lib/utils/safe-storage";
import { useRootLoaderData } from "~/root";

import { buildIdentifyContactTraits } from "./identify-traits";
import { getDeviceType, hashEmail, waitForAnalytics } from "./utils";

const SESSION_KEY = "segment_identified";

export const SegmentIdentify = () => {
  const { customer, loyaltyPoints } = useRootLoaderData();
  const { site } = useSite();
  const { learningCenterData, getActiveGoal } = useLearningCenter();

  // Destructure primitives so effects key on values, not the object reference
  // (loader returns a fresh object on every revalidation).
  const email = customer?.email;
  const firstName = customer?.firstName;
  const lastName = customer?.lastName;
  // COM-417 item 5: phone + preferred/first address, SFRA identify parity.
  // Serialized so the effect keys on the trait VALUES, not the fresh
  // `addresses` array reference every revalidation produces.
  const contactTraitsKey = JSON.stringify(
    buildIdentifyContactTraits({
      phoneHome: customer?.phoneHome,
      addresses: customer?.addresses,
    }),
  );
  // LC-AN-03: HSA persona traits are HSA-only. `bySiteId` keeps this exhaustive
  // over FSA/HSA/WDH (and satisfies the multi-site lint gate).
  const personaEnabled = bySiteId(site.id, {
    FSASTORE: false,
    HSASTORE: true,
    WDH: false,
  });

  // LC-AN-03: HSA persona traits. `currentHSAPersona` = active goal id;
  // `previousHSAPersona` = most-recent non-active goal id. Both are OMITTED on
  // FSA/WDH and whenever the underlying goal is absent — never a null/empty
  // persona on the wire (persona links a health-related goal id to the hashed
  // userId, so an empty value would be noise on a sensitive dimension).
  const activeGoalId = personaEnabled ? getActiveGoal()?.id : undefined;
  const previousGoalId = useMemo(
    () =>
      personaEnabled ? getMostRecentNonActiveGoal(learningCenterData) : null,
    [personaEnabled, learningCenterData],
  );

  // Re-identify only when the trait set changes (extends the old email-only
  // dedup so a persona change re-fires identify). Mirror composable
  // IdentifySegmentUser `lastIdentifiedTraits`.
  // SFCC-11876 — safeSessionStorage, NOT raw `sessionStorage`: `typeof window`
  // does not guard the getter, which throws SecurityError under Chrome "block
  // all cookies" — a render-time throw here downs EVERY page's root boundary.
  const lastIdentifiedTraits = useRef<string | null>(
    safeSessionStorage.getItem(SESSION_KEY),
  );

  // Mirror composable IdentifySegmentUser: clear the dedup on logout so identify
  // re-fires on the next login, including same-user re-login in the same tab.
  useEffect(() => {
    if (!email) {
      lastIdentifiedTraits.current = null;
      safeSessionStorage.removeItem(SESSION_KEY);
    }
  }, [email]);

  useEffect(() => {
    if (!email) return;

    let cancelled = false;
    const run = async () => {
      await waitForAnalytics();
      if (cancelled || !window.analytics) return;

      const userId = await hashEmail(email);
      const payload: Record<string, unknown> = {
        device: getDeviceType(),
        sales_channel: site.id,
        email,
      };
      if (firstName) payload.first_name = firstName;
      if (lastName) payload.last_name = lastName;
      // phone / address_city / address_country / address_state /
      // address_zipcode — keys omitted when absent (identify-traits.ts).
      Object.assign(payload, JSON.parse(contactTraitsKey));
      // ANALYTICS-24: per-site Annex Cloud loyalty credit key. FSA/HSA only;
      // WDH has no key (and no Segment write key, so identify never fires there).
      // Value is the persisted available-points read in the root loader.
      const creditKey = bySiteId(site.id, {
        FSASTORE: "fsa_store_credit_points",
        HSASTORE: "hsa_store_credit_points",
        WDH: undefined,
      });
      // Unread balance: no trait, so Segment keeps the last real one.
      if (creditKey && loyaltyPoints !== undefined)
        payload[creditKey] = loyaltyPoints;

      // LC-AN-03: HSA-only persona traits — keys omitted entirely when absent.
      if (activeGoalId) payload.currentHSAPersona = activeGoalId;
      if (previousGoalId) payload.previousHSAPersona = previousGoalId;

      // Dedup on the full trait hash (not just email) so a persona change
      // re-identifies, but a no-op revalidation does not.
      const traitsKey = JSON.stringify(payload);
      if (lastIdentifiedTraits.current === traitsKey) return;

      // Persist before awaiting identify so a concurrent effect re-run
      // (e.g., from a navigation) doesn't fire a second call.
      lastIdentifiedTraits.current = traitsKey;
      safeSessionStorage.setItem(SESSION_KEY, traitsKey);
      await window.analytics.identify(userId, payload);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    email,
    firstName,
    lastName,
    contactTraitsKey,
    loyaltyPoints,
    site.id,
    activeGoalId,
    previousGoalId,
  ]);

  return null;
};
